import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash, FilePdf, UploadSimple, ArrowLeft, Sparkle, DownloadSimple, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api, { API } from "../../lib/api";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EXP_CATEGORIES = ["Materials", "Logistics", "Labour", "Equipment", "Travel", "Services", "Other"];
const PAY_MODES = ["bank_transfer", "cheque", "cash", "upi", "other"];

const empPay = { date: new Date().toISOString().slice(0,10), amount: 0, currency: "INR", fx_rate: 1, mode: "bank_transfer", reference: "", notes: "" };
const empExp = { date: new Date().toISOString().slice(0,10), amount: 0, currency: "INR", fx_rate: 1, category: "Materials", description: "", gst_percent: 18, vendor_id: null };

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [payForm, setPayForm] = useState(empPay);
  const [expForm, setExpForm] = useState(empExp);
  const [expExtracting, setExpExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const expExtractRef = useRef(null);
  const woFileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/finance/work-orders/${id}`);
    setWo(data);
    const { data: vs } = await api.get("/finance/contacts", { params: { company_id: data.company_id, type: "vendor" } });
    setVendors(vs);
  };

  useEffect(() => { load(); }, [id]);

  if (!wo) return <Layout title="Work Order"><div className="p-10 text-center text-[#717680]">Loading…</div></Layout>;

  const s = wo.summary || {};

  const savePayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error("Amount required"); return; }
    const payload = { ...payForm, work_order_id: id, amount: parseFloat(payForm.amount), fx_rate: parseFloat(payForm.fx_rate || 1) };
    await api.post("/finance/payments", payload);
    toast.success("Payment recorded"); setPayOpen(false); setPayForm(empPay); load();
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Delete payment?")) return;
    await api.delete(`/finance/payments/${pid}`);
    toast.success("Deleted"); load();
  };

  const saveExpense = async () => {
    if (!expForm.amount || Number(expForm.amount) <= 0) { toast.error("Amount required"); return; }
    const payload = {
      ...expForm, work_order_id: id,
      amount: parseFloat(expForm.amount), fx_rate: parseFloat(expForm.fx_rate || 1),
      gst_percent: parseFloat(expForm.gst_percent || 0),
      vendor_id: expForm.vendor_id || null,
    };
    await api.post("/finance/expenses", payload);
    toast.success("Expense recorded"); setExpOpen(false); setExpForm(empExp); load();
  };

  const removeExpense = async (eid) => {
    if (!window.confirm("Delete expense?")) return;
    await api.delete(`/finance/expenses/${eid}`);
    toast.success("Deleted"); load();
  };

  const handleExpExtract = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setExpExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/finance/extract?kind=expense", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExpForm((curr) => ({
        ...curr,
        amount: data.amount ?? curr.amount,
        date: data.date || curr.date,
        currency: data.currency === "USD" ? "USD" : "INR",
        gst_percent: data.gst_percent ?? curr.gst_percent,
        category: EXP_CATEGORIES.includes(data.category) ? data.category : curr.category,
        description: data.description || (data.vendor_name ? `Bill from ${data.vendor_name}` : curr.description),
      }));
      toast.success("AI extracted fields");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally { setExpExtracting(false); e.target.value=""; }
  };

  const uploadWoFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      await api.post(`/finance/work-orders/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value=""; }
  };

  return (
    <Layout
      title={wo.title}
      subtitle={`${wo.type.replace("_"," ").toUpperCase()} · ${wo.po_number}`}
      actions={
        <button onClick={()=>navigate("/finance/work-orders")} className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white">
          <ArrowLeft size={16} weight="bold"/> Back
        </button>
      }
    >
      {/* P&L band */}
      <div className="grid grid-cols-1 md:grid-cols-5 border border-black/10 bg-white mb-6" data-testid="wo-summary-band">
        {[
          ["Order Value", s.value_inr, "#0A0A0A"],
          ["Received", s.total_payments_inr, "#2A9D8F"],
          ["Outstanding", s.outstanding_inr, "#F4A261"],
          ["Expenses", s.total_expenses_inr, "#D90429"],
          ["Net Profit", s.net_profit_inr, s.net_profit_inr>=0?"#2A9D8F":"#D90429"],
        ].map(([label, val, color])=>(
          <div key={label} className="p-5 border-r border-black/5 last:border-r-0">
            <div className="overline" style={{color}}>{label}</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-2" style={{color}}>{formatINR(val)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        {/* Order details */}
        <div className="lg:col-span-5 border border-black/10 bg-white p-6">
          <div className="overline mb-4">Order Details</div>
          <div className="grid grid-cols-2 gap-y-3 text-sm">
            <div className="text-[#717680]">PO/WO #</div><div className="font-mono-shelv">{wo.po_number}</div>
            <div className="text-[#717680]">Currency</div><div>{wo.currency} {wo.currency==="USD" && `(₹${wo.fx_rate}/$)`}</div>
            <div className="text-[#717680]">Original value</div><div className="font-mono-shelv">{wo.currency==="USD"?`$${Number(wo.value).toLocaleString("en-US")}`:formatINR(wo.value)}</div>
            <div className="text-[#717680]">Status</div><div className="uppercase font-mono-shelv text-xs">{wo.status}</div>
            <div className="text-[#717680]">Start</div><div>{wo.start_date || "—"}</div>
            <div className="text-[#717680]">End</div><div>{wo.end_date || "—"}</div>
            <div className="text-[#717680]">Overhead applied</div><div className="font-mono-shelv">{s.overhead_percent}% ({formatINR(s.overhead_inr)})</div>
            <div className="text-[#717680]">Gross profit</div><div className="font-mono-shelv">{formatINR(s.gross_profit_inr)}</div>
            <div className="text-[#717680]">Net margin</div><div className="font-mono-shelv font-bold">{s.net_margin_percent}%</div>
          </div>
          {wo.description && <p className="mt-4 pt-4 border-t border-black/5 text-sm text-[#4A4D57]">{wo.description}</p>}

          <div className="mt-5 pt-4 border-t border-black/5">
            <input ref={woFileRef} type="file" accept="application/pdf,image/*" onChange={uploadWoFile} className="hidden" data-testid="wo-file-input"/>
            {wo.file_path ? (
              <div className="flex items-center gap-2 border border-[#D90429]/30 bg-[#D90429]/5 px-3 py-2" data-testid="wo-file-attached">
                <FilePdf size={18} weight="bold" className="text-[#D90429] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{wo.file_name || "Document"}</div>
                  <a href={`${API}/finance/work-orders/${wo.id}/file`} target="_blank" rel="noreferrer" className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#D90429]" data-testid="view-wo-file">View</a>
                </div>
                <button onClick={()=>woFileRef.current?.click()} className="text-[10px] font-mono-shelv uppercase text-[#717680] hover:text-[#0A0A0A] px-2" data-testid="replace-wo-file">Replace</button>
              </div>
            ) : (
              <button onClick={()=>woFileRef.current?.click()} disabled={uploading} className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-black/20 hover:border-[#D90429] hover:bg-[#D90429]/5 text-sm text-[#4A4D57] hover:text-[#D90429] py-2.5 transition-all" data-testid="upload-wo-file">
                <UploadSimple size={16} weight="bold"/> {uploading?"Uploading…":"Attach scanned PO/WO"}
              </button>
            )}
          </div>
        </div>

        {/* Payments + Expenses */}
        <div className="lg:col-span-7 space-y-6">
          {/* Payments */}
          <div className="border border-black/10 bg-white">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <div><div className="overline" style={{color:"#2A9D8F"}}>Revenue · Payments received</div><div className="font-display text-xl font-bold mt-1">{formatINR(s.total_payments_inr)} <span className="text-sm text-[#717680] font-normal font-mono-shelv">of {formatINR(s.value_inr)}</span></div></div>
              <button onClick={()=>{setPayForm(empPay); setPayOpen(true);}} data-testid="add-payment-button" className="inline-flex items-center gap-2 px-3 py-1.5 border border-black/15 text-xs font-semibold hover:bg-[#2A9D8F] hover:text-white hover:border-[#2A9D8F]"><Plus size={14}/> Payment</button>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="payments-table">
              <thead className="bg-[#F5F6F8] text-left text-xs"><tr>{["Date","Amount","Mode","Reference",""].map(h=>(<th key={h} className="px-4 py-2 font-mono-shelv uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
              <tbody>
                {(wo.payments||[]).length===0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-[#717680]">No payments yet.</td></tr>}
                {(wo.payments||[]).map(p=>(
                  <tr key={p.id} className="border-t border-black/5">
                    <td className="px-4 py-2">{p.date}</td>
                    <td className="px-4 py-2 font-mono-shelv">{p.currency==="USD"?`$${p.amount}`:formatINR(p.amount)} {p.currency==="USD" && <span className="text-[10px] text-[#717680]">@₹{p.fx_rate}</span>}</td>
                    <td className="px-4 py-2 text-[#4A4D57]">{p.mode}</td>
                    <td className="px-4 py-2 text-[#4A4D57] font-mono-shelv text-xs">{p.reference}</td>
                    <td className="px-4 py-2 text-right"><button onClick={()=>removePayment(p.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-payment-${p.id}`}><Trash size={13}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Expenses */}
          <div className="border border-black/10 bg-white">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <div><div className="overline" style={{color:"#D90429"}}>Costs · Expenses against this order</div><div className="font-display text-xl font-bold mt-1">{formatINR(s.total_expenses_inr)}</div></div>
              <button onClick={()=>{setExpForm(empExp); setExpOpen(true);}} data-testid="add-expense-button" className="inline-flex items-center gap-2 px-3 py-1.5 border border-black/15 text-xs font-semibold hover:bg-[#D90429] hover:text-white hover:border-[#D90429]"><Plus size={14}/> Expense</button>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="expenses-table">
              <thead className="bg-[#F5F6F8] text-left text-xs"><tr>{["Date","Amount","Category","Vendor","Description",""].map(h=>(<th key={h} className="px-4 py-2 font-mono-shelv uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
              <tbody>
                {(wo.expenses||[]).length===0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#717680]">No expenses yet.</td></tr>}
                {(wo.expenses||[]).map(e=>{
                  const vendor = vendors.find(v=>v.id===e.vendor_id);
                  return (
                    <tr key={e.id} className="border-t border-black/5">
                      <td className="px-4 py-2">{e.date}</td>
                      <td className="px-4 py-2 font-mono-shelv">{e.currency==="USD"?`$${e.amount}`:formatINR(e.amount)} {e.currency==="USD" && <span className="text-[10px] text-[#717680]">@₹{e.fx_rate}</span>}</td>
                      <td className="px-4 py-2 text-[#4A4D57]"><span className="text-xs font-mono-shelv uppercase tracking-widest">{e.category}</span></td>
                      <td className="px-4 py-2 text-[#4A4D57]">{vendor?.name || "—"}</td>
                      <td className="px-4 py-2 text-[#4A4D57] truncate max-w-[12rem]">{e.description}</td>
                      <td className="px-4 py-2 text-right">
                        {e.file_path && <a href={`${API}/finance/expenses/${e.id}/file`} target="_blank" rel="noreferrer" className="p-1.5 inline-block hover:bg-black/5" data-testid={`view-expense-file-${e.id}`}><DownloadSimple size={13}/></a>}
                        <button onClick={()=>removeExpense(e.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-expense-${e.id}`}><Trash size={13}/></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Record payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e)=>setPayForm({...payForm, date:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Mode</Label>
              <Select value={payForm.mode} onValueChange={(v)=>setPayForm({...payForm, mode:v})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{PAY_MODES.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={payForm.currency} onValueChange={(v)=>setPayForm({...payForm, currency:v, fx_rate: v==="INR"?1:payForm.fx_rate})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Amount</Label><Input data-testid="payment-amount-input" type="number" value={payForm.amount} onChange={(e)=>setPayForm({...payForm, amount:e.target.value})} className="rounded-none"/></div>
            {payForm.currency==="USD" && <div className="col-span-2"><Label>USD→INR rate</Label><Input type="number" value={payForm.fx_rate} onChange={(e)=>setPayForm({...payForm, fx_rate:e.target.value})} className="rounded-none"/></div>}
            <div className="col-span-2"><Label>Reference / UTR</Label><Input value={payForm.reference} onChange={(e)=>setPayForm({...payForm, reference:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={payForm.notes} onChange={(e)=>setPayForm({...payForm, notes:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setPayOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={savePayment} data-testid="save-payment-button" className="px-4 py-2 bg-[#2A9D8F] text-white text-sm font-semibold hover:bg-[#218377]">Record</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense dialog */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Add expense</DialogTitle></DialogHeader>

          <div className="flex items-center justify-between border-b border-black/5 pb-3 mb-1">
            <p className="text-xs text-[#717680]">Upload the bill PDF — AI will auto-fill.</p>
            <input ref={expExtractRef} type="file" accept="application/pdf,image/*" onChange={handleExpExtract} className="hidden" data-testid="extract-expense-input"/>
            <button onClick={()=>expExtractRef.current?.click()} disabled={expExtracting} data-testid="extract-expense-button" className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#D90429] disabled:opacity-50">
              <Sparkle size={14} weight="fill"/> {expExtracting ? "Reading PDF…" : "AI extract bill"}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={expForm.date} onChange={(e)=>setExpForm({...expForm, date:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Category</Label>
              <Select value={expForm.category} onValueChange={(v)=>setExpForm({...expForm, category:v})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{EXP_CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={expForm.currency} onValueChange={(v)=>setExpForm({...expForm, currency:v, fx_rate: v==="INR"?1:expForm.fx_rate})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Amount (incl. GST)</Label><Input data-testid="expense-amount-input" type="number" value={expForm.amount} onChange={(e)=>setExpForm({...expForm, amount:e.target.value})} className="rounded-none"/></div>
            {expForm.currency==="USD" && <div className="col-span-2"><Label>USD→INR rate</Label><Input type="number" value={expForm.fx_rate} onChange={(e)=>setExpForm({...expForm, fx_rate:e.target.value})} className="rounded-none"/></div>}
            <div><Label>GST %</Label><Input type="number" value={expForm.gst_percent} onChange={(e)=>setExpForm({...expForm, gst_percent:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Vendor</Label>
              <Select value={expForm.vendor_id || "none"} onValueChange={(v)=>setExpForm({...expForm, vendor_id: v==="none"?null:v})}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {vendors.map(v=><SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={expForm.description} onChange={(e)=>setExpForm({...expForm, description:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setExpOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={saveExpense} data-testid="save-expense-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Add</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
